import { useEffect, useState, useContext } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { FileUpload } from "primereact/fileupload";
import { Dropdown } from "primereact/dropdown";
import * as XLSX from "xlsx";
import { getSources } from "../../api/sourceApi";
import { AuthContext } from "../../Context/AuthContext";

export default function ProductForm({ visible, onHide, onSubmit, initialData, uploadMode = false }) {
  const [form, setForm] = useState({ name: "", source: "" });
  const [preview, setPreview] = useState([]);
  const [errors, setErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sources, setSources] = useState([]);
  const { auth } = useContext(AuthContext);
  const token = auth?.token;

  useEffect(() => {
    if (!uploadMode) {
      if (initialData) setForm({ name: initialData.name || "", source: initialData.source || "" });
      else setForm({ name: "", source: "" });
    } else {
      setPreview([]);
      setErrors([]);
    }
  }, [initialData, uploadMode, visible]);

  useEffect(() => {
    const fetchSources = async () => {
      if (!token) return;
      try {
        const res = await getSources(token);
        setSources(res.data.data || []);
      } catch (err) {
        console.error("Failed to load sources", err);
      }
    };
    fetchSources();
  }, [token]);

  const sentenceCase = str => {
    if (!str) return "";
    return str.toLowerCase().replace(/(^\s*\w|[.!?]\s*\w)/g, c => c.toUpperCase());
  };

  const handleFile = async (event) => {
    const file = event.files ? event.files[0] : event.target.files[0];
    if (!file) return;

    let data = [];
    try {
      if (file.name.endsWith(".json")) {
        const text = await file.text();
        data = JSON.parse(text);
      } else if (file.name.endsWith(".xlsx") || file.name.endsWith(".xls")) {
        const buffer = await file.arrayBuffer();
        const wb = XLSX.read(buffer);
        const ws = wb.Sheets[wb.SheetNames[0]];
        data = XLSX.utils.sheet_to_json(ws);
      }
    } catch {
      alert("Error reading file. Please check the file format.");
      return;
    }

    if (!Array.isArray(data)) data = [];

    const uniqueMap = {};
    const uniqueData = [];
    const newErrors = [];

    data.forEach((p, i) => {
      const name = sentenceCase(p.name || p.product || "").trim();
      const source = p.source?.trim() || "";
      if (!name) {
        newErrors.push(`Row ${i + 1}: Product name is required`);
        return;
      }
      const key = name.toLowerCase();
      if (uniqueMap[key]) {
        newErrors.push(`Duplicate product found: ${name}`);
        return;
      }
      uniqueMap[key] = true;
      uniqueData.push({ name, source });
    });

    setPreview(uniqueData);
    setErrors(newErrors);
  };

  const handlePreviewChange = (index, field, value) => {
    const newPreview = [...preview];
    newPreview[index][field] = field === "name" ? sentenceCase(value) : value;
    setPreview(newPreview);

    const seen = {};
    const newErrors = [];
    newPreview.forEach((p, i) => {
      const key = p.name.trim().toLowerCase();
      if (!p.name.trim()) newErrors.push(`Row ${i + 1}: Product name is required`);
      else if (seen[key]) newErrors.push(`Duplicate product found: ${p.name}`);
      seen[key] = true;
    });
    setErrors(newErrors);
  };

  const handleDeleteRow = index => {
    const newPreview = [...preview];
    newPreview.splice(index, 1);
    setPreview(newPreview);

    const seen = {};
    const newErrors = [];
    newPreview.forEach((p, i) => {
      const key = p.name.trim().toLowerCase();
      if (!p.name.trim()) newErrors.push(`Row ${i + 1}: Product name is required`);
      else if (seen[key]) newErrors.push(`Duplicate product found: ${p.name}`);
      seen[key] = true;
    });
    setErrors(newErrors);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      if (uploadMode) {
        if (!preview.length) {
          alert("No valid data to upload");
          return;
        }
        if (errors.length) {
          alert("Please fix the following errors before upload:\n" + errors.join("\n"));
          return;
        }
        await onSubmit(preview.map(p => ({
          name: p.name.trim(),
          source: p.source?.trim() || null
        })));
        return;
      }

      if (!form.name) {
        alert("Product name is required");
        return;
      }

      await onSubmit({ name: sentenceCase(form.name), source: form.source || null });
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = e => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Dialog
      header={uploadMode ? "Upload Products" : initialData ? "Edit Product" : "Add Product"}
      visible={visible}
      onHide={onHide}
      modal
      className="w-6"
    >
      <div className="p-3 flex flex-column gap-3" onKeyDown={handleKeyDown}>

        {!uploadMode && (
          <div className="flex flex-column gap-3">
            <div className="flex flex-column gap-1">
              <label>Product Name</label>
              <InputText
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div className="flex flex-column gap-1">
              <label>Source (optional)</label>
              <Dropdown
                value={form.source}
                options={sources.map(s => ({ label: s.key_id, value: s.key_id }))}
                onChange={e => setForm({ ...form, source: e.value })}
                editable
                placeholder="Select or type source"
                panelStyle={{ maxWidth: "220px" }}
              />
            </div>
          </div>
        )}

        {uploadMode && (
          <div className="flex flex-column gap-4 align-items-center">
            {preview.length === 0 && (
              <div className="flex justify-content-center">
                <FileUpload
                  mode="basic"
                  accept=".json,.xlsx,.xls"
                  chooseLabel="Upload"
                  onSelect={handleFile}
                  auto
                  chooseOptions={{
              icon: "pi pi-upload",
              style: {
                backgroundColor: "#d87347ff",
                color: "#fff",
                border: "none",
                borderRadius: 4,
                padding: "0.5rem 1rem",
                cursor: "pointer",
              },
            }}
            style={{
              border: "2px dashed #bebbbaff",
              borderRadius: 8,
              padding: 50,
              background: "#f8f9fa",
            }}
                />
              </div>
            )}

            {errors.length > 0 && (
              <div
                className="p-3 w-full"
                style={{
                  maxHeight: 120,
                  overflowY: "auto",
                  backgroundColor: '#ffe6e6',
                  border: '1px solid #f5c6cb',
                  borderRadius: '8px',
                  color: '#721c24'
                }}
              >
                <b>Errors:</b>
                <ul className="m-0 pl-3">
                  {errors.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              </div>
            )}

            {preview.length > 0 && (
              <div
                className="w-full"
                style={{
                  maxHeight: 300,
                  overflow: "auto",
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              >
                <table className="p-datatable-table w-full">
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ padding: '12px', borderBottom: '2px solid #dee2e6' }}>Name</th>
                      <th style={{ padding: '12px', borderBottom: '2px solid #dee2e6' }}>Source (optional)</th>
                      <th style={{ padding: '12px', borderBottom: '2px solid #dee2e6' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((p, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #dee2e6' }}>
                        <td style={{ padding: '8px' }}>
                          <InputText
                            value={p.name}
                            onChange={e => handlePreviewChange(i, "name", e.target.value)}
                            style={{ width: "100%", borderRadius: '4px' }}
                          />
                        </td>
                        <td style={{ padding: '8px' }}>
                          <Dropdown
                            value={p.source}
                            options={sources.map(s => ({ label: s.key_id, value: s.key_id }))}
                            onChange={e => handlePreviewChange(i, "source", e.value)}
                            editable
                            placeholder="Select or type source"
                            style={{ width: "100%", borderRadius: '4px' }}
                            panelStyle={{ maxWidth: "220px" }}
                          />
                        </td>
                        <td style={{ padding: '8px', textAlign: 'center' }}>
                          <Button
                            icon="pi pi-trash"
                            className="p-button-danger p-button-text p-button-rounded"
                            onClick={() => handleDeleteRow(i)}
                            style={{ color: '#dc3545' }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <Button
          label={isLoading ? (uploadMode ? "Uploading..." : "Submitting...") : (uploadMode ? "Upload" : "Submit")}
          icon={isLoading ? "pi pi-spin pi-spinner" : ""}
          onClick={handleSubmit}
          disabled={isLoading}
          className=" p-button-block custom-splitbutton"
          style={{ backgroundColor: "#FC5F1CFF", color: "#fff" }}
        />
      </div>
    </Dialog>
  );
}
